import { isRecord } from '~/services/layout/layout.ts';

export type PopupMenuAssetFlavour = 'flavour_1' | 'flavour_2';

export type PopupMenuAssetType = 'cell_action' | 'button';

export type ResolvePopupMenuAssetArgs = {
  customConfiguration: unknown;
  surface: PopupMenuAssetType;
  flavour: unknown;
};

/**
 * Static More assets used when the plugin has no uploaded asset for a
 * surface and flavour.
 */
export const POPUP_MENU_DEFAULT_MORE_ASSET: Record<
  PopupMenuAssetType,
  Record<PopupMenuAssetFlavour, string>
> = {
  cell_action: {
    flavour_1: '/popup-menu/more-cell-action-flavor-1.svg',
    flavour_2: '/popup-menu/more-cell-action-flavor-2.svg',
  },
  button: {
    flavour_1: '/popup-menu/more-button-flavor-1.svg',
    flavour_2: '/popup-menu/more-button-flavor-2.svg',
  },
};

const FLAVOUR_KEY_INDEX: Record<PopupMenuAssetFlavour, number> = {
  flavour_1: 1,
  flavour_2: 2,
};

/**
 * Normalizes a cell/button `action_asset_flavour` value; anything other than
 * `flavour_2` falls back to `flavour_1`.
 */
export function normalizePopupMenuAssetFlavour(
  flavour: unknown
): PopupMenuAssetFlavour {
  if (
    typeof flavour === 'string' &&
    flavour.trim().toLowerCase() === 'flavour_2'
  ) {
    return 'flavour_2';
  }

  return 'flavour_1';
}

/**
 * Resolves the popup menu trigger asset for a surface and flavour from the
 * plugin configuration (e.g. `cell_action_assets_flavor_2_asset`), falling
 * back to that surface and flavour's static More asset when none is uploaded.
 */
export function resolvePopupMenuAsset({
  customConfiguration,
  surface,
  flavour,
}: ResolvePopupMenuAssetArgs): string {
  const normalizedFlavour: PopupMenuAssetFlavour =
    normalizePopupMenuAssetFlavour(flavour);

  const configuredAsset: unknown = isRecord(customConfiguration)
    ? customConfiguration[
        `${surface}_assets_flavor_${FLAVOUR_KEY_INDEX[normalizedFlavour]}_asset`
      ]
    : undefined;

  if (typeof configuredAsset === 'string' && configuredAsset.trim()) {
    return configuredAsset;
  }

  return POPUP_MENU_DEFAULT_MORE_ASSET[surface][normalizedFlavour];
}
